import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { ArrowLeft, TrendingUp, DollarSign, BarChart3, Lock, Save, X, Check, Trophy, Plus } from 'lucide-react';
import { ResultsDisclaimer, DealScoreDisclaimer, Footer } from '../components/LegalDisclaimers';
import { RendPlexLogo } from '../components/RendPlexLogo';
import DarkModeToggle from '../components/DarkModeToggle';
import LanguageSwitcher from '../components/LanguageSwitcher';
import useFormatters from '../lib/useFormatters';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Compare() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const fmt = useFormatters();
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [proBlocked, setProBlocked] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [saving, setSaving] = useState(false);
  const [dealIds, setDealIds] = useState([]);

  useEffect(() => {
    const loadDeals = async () => {
      const ids = location.state?.dealIds;
      if (!ids || ids.length < 2) {
        toast.error(t('dealCompare.selectDeals'));
        navigate('/dashboard');
        return;
      }
      setDealIds(ids);

      try {
        const response = await axios.post(
          `${BACKEND_URL}/api/deals/compare`,
          ids,
          { withCredentials: true }
        );
        setDeals(response.data);
      } catch (error) {
        if (error.response?.status === 403) {
          setProBlocked(true);
        } else {
          console.error('Failed to load deals:', error);
          toast.error(t('dealCompare.loadFailed'));
          navigate('/dashboard');
        }
      } finally {
        setLoading(false);
      }
    };

    loadDeals();
  }, [location.state, navigate, t]);

  const saveComparison = async () => {
    if (!saveName.trim()) {
      toast.error(t('dealCompare.nameRequired'));
      return;
    }
    try {
      setSaving(true);
      await axios.post(`${BACKEND_URL}/api/comparisons`, {
        name: saveName.trim(),
        deal_ids: dealIds,
      }, { withCredentials: true });
      toast.success(t('dealCompare.saved'));
      setShowSaveModal(false);
      setSaveName('');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('dealCompare.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="mt-4 text-slate-600 dark:text-slate-400">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (proBlocked) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] dark:bg-[#0B0E14] text-foreground transition-colors duration-300">
        <nav className="border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-4">
            <div className="flex justify-between items-center">
              <Link to="/dashboard" className="flex items-center gap-2">
                <RendPlexLogo size="md" />
              </Link>
              <div className="flex items-center gap-3">
                <DarkModeToggle />
                <LanguageSwitcher variant="minimal" />
              </div>
            </div>
          </div>
        </nav>
        <div className="max-w-lg mx-auto px-4 py-24 text-center">
          <div className="w-20 h-20 bg-[#45C04A]/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Lock className="w-10 h-10 text-[#45C04A]" />
          </div>
          <h1 className="font-primary font-bold text-3xl text-[#12355B] dark:text-white mb-3">{t('dealCompare.proRequired')}</h1>
          <p className="text-slate-600 dark:text-slate-400 mb-8">{t('dealCompare.proRequiredDesc')}</p>
          <div className="flex gap-3 justify-center">
            <Button
              data-testid="compare-upgrade-button"
              onClick={() => navigate('/pricing')}
              className="bg-[#12355B] hover:bg-[#12355B]/90 dark:bg-[#45C04A] dark:hover:bg-[#65D95A] dark:text-[#12355B] text-white rounded-sm px-8 py-3"
            >
              {t('pricing.upgradeNow')}
            </Button>
            <Button
              data-testid="compare-back-button"
              onClick={() => navigate('/dashboard')}
              variant="outline"
              className="rounded-sm px-6 py-3"
            >
              {t('nav.backToDashboard')}
            </Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const metrics = [
    { key: 'deal_score', label: t('dealCompare.metrics.dealScore'), format: (v) => fmt.number(v) },
    { key: 'purchase_price', label: t('dealCompare.metrics.purchasePrice'), format: (v) => fmt.money0(v) },
    { key: 'monthly_cash_flow', label: t('dealCompare.metrics.monthlyCashFlow'), format: (v) => fmt.money2(v) },
    { key: 'annual_cash_flow', label: t('dealCompare.metrics.annualCashFlow'), format: (v) => fmt.money0(v) },
    { key: 'roi', label: t('dealCompare.metrics.roi'), format: (v) => fmt.percent(v) },
    { key: 'cap_rate', label: t('dealCompare.metrics.capRate'), format: (v) => fmt.percent(v) },
    { key: 'noi', label: t('dealCompare.metrics.noi'), format: (v) => fmt.money0(v) },
    { key: 'cash_on_cash', label: t('dealCompare.metrics.cashOnCash'), format: (v) => fmt.percent(v) },
    { key: 'monthly_income', label: t('dealCompare.metrics.monthlyIncome'), format: (v) => fmt.money0(v) },
    { key: 'monthly_mortgage', label: t('dealCompare.metrics.monthlyMortgage'), format: (v) => fmt.money2(v) },
    { key: 'monthly_expenses', label: t('dealCompare.metrics.monthlyExpenses'), format: (v) => fmt.money2(v) },
    { key: 'suggested_offer_price', label: t('dealCompare.metrics.suggestedOffer'), format: (v) => fmt.money0(v) },
  ];

  const getBestValue = (metricKey) => {
    if (['deal_score', 'roi', 'cap_rate', 'monthly_cash_flow', 'annual_cash_flow', 'monthly_income', 'noi', 'cash_on_cash'].includes(metricKey)) {
      return Math.max(...deals.map(d => d[metricKey]));
    } else if (['purchase_price', 'monthly_mortgage', 'monthly_expenses'].includes(metricKey)) {
      return Math.min(...deals.map(d => d[metricKey]));
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] dark:bg-[#0B0E14] text-foreground transition-colors duration-300">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 backdrop-blur-xl bg-white/70 dark:bg-[#0B0E14]/70 backdrop-saturate-150 border-b border-slate-200/50 dark:border-slate-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-4">
          <div className="flex justify-between items-center">
            <Link to="/dashboard" className="flex items-center gap-2">
              <RendPlexLogo size="md" />
            </Link>
            <div className="flex items-center gap-3">
              <DarkModeToggle />
              <LanguageSwitcher variant="minimal" />
              <Button
                data-testid="back-to-dashboard-button"
                onClick={() => navigate('/dashboard')}
                variant="ghost"
                className="text-slate-500 dark:text-slate-400 hover:text-[#12355B] dark:hover:text-white"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                {t('nav.backToDashboard')}
              </Button>
              <Button data-testid="new-analysis-button" onClick={() => navigate('/analyze')} size="sm"
                className="bg-[#12355B] hover:bg-[#1565C0] text-white dark:bg-[#45C04A] dark:hover:bg-[#65D95A] dark:text-[#0F172A] font-semibold">
                <Plus className="w-4 h-4 mr-1.5" /> {t('dashboard.newAnalysis')}
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="relative isolate max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-12">
        {/* Background Image */}
        <div className="absolute inset-0 -mx-4 sm:-mx-6 lg:-mx-12 overflow-hidden -z-10">
          <img
            src="https://static.prod-images.emergentagent.com/jobs/35251cdc-fe6f-4305-b40a-115bc9637197/images/5cac38517c03f4a4d3f18f4002c26542ed8d9f2720a2e70f5ab08875097ca05e.jpeg"
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#F8FAFC]/[0.96] dark:bg-[#0B0E14]/[0.97] backdrop-blur-[2px]" />
        </div>
        <div className="mb-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="font-primary font-bold text-4xl text-[#12355B] dark:text-white mb-2">{t('dealCompare.title')}</h1>
            <p className="text-slate-600 dark:text-slate-400">{t('dealCompare.subtitle')}</p>
          </div>
          <Button
            data-testid="save-comparison-button"
            onClick={() => setShowSaveModal(true)}
            className="bg-[#12355B] hover:bg-[#12355B]/90 dark:bg-[#45C04A] dark:hover:bg-[#65D95A] dark:text-[#12355B] text-white rounded-sm px-5 py-2.5"
          >
            <Save className="w-4 h-4 mr-2" />
            {t('dealCompare.saveComparison')}
          </Button>
        </div>

        <DealScoreDisclaimer />

        {/* Comparison Table */}
        <div className="bg-white dark:bg-[#131824] border border-slate-200 dark:border-slate-800 rounded-2xl overflow-x-auto mt-6">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-[#0B0E14]">
                <th className="text-left p-5 font-primary font-bold text-[#12355B] dark:text-white sticky left-0 bg-slate-50 dark:bg-[#0B0E14] z-10">
                  {t('dealCompare.metric')}
                </th>
                {deals.map((deal, idx) => {
                  const isOverallWinner = deal.deal_score === Math.max(...deals.map(d => d.deal_score));
                  return (
                    <th key={deal.deal_id} className={`text-left p-5 min-w-[250px] relative ${isOverallWinner ? 'bg-[#45C04A]/[0.04] dark:bg-[#45C04A]/[0.06]' : ''}`}>
                      <div className="mb-3 mt-1">
                        <div className="relative inline-flex items-center justify-center w-16 h-16 mb-2">
                          <svg className="w-16 h-16 -rotate-90" viewBox="0 0 64 64">
                            <circle cx="32" cy="32" r="28" fill="none" stroke="currentColor" className="text-slate-100 dark:text-slate-800" strokeWidth="4" />
                            <circle cx="32" cy="32" r="28" fill="none"
                              stroke={deal.deal_score >= 70 ? '#45C04A' : deal.deal_score >= 40 ? '#F59E0B' : '#EF4444'}
                              strokeWidth="4" strokeLinecap="round"
                              strokeDasharray={`${(deal.deal_score / 100) * 176} 176`} />
                          </svg>
                          <span className={`absolute text-xl font-primary font-black ${
                            deal.deal_score >= 70 ? 'text-[#45C04A]' : deal.deal_score >= 40 ? 'text-amber-500' : 'text-red-500'
                          }`}>
                            {deal.deal_score}
                          </span>
                        </div>
                      </div>
                      <div className={`font-primary font-bold text-sm line-clamp-2 text-[#12355B] dark:text-white`}>
                        {deal.property_address}
                      </div>
                      <Button
                        data-testid={`view-deal-${idx}`}
                        onClick={() => navigate(`/deal/${deal.deal_id}`)}
                        variant="ghost"
                        size="sm"
                        className="text-xs mt-2 text-slate-500 hover:text-[#12355B] dark:hover:text-white"
                      >
                        {t('dealCompare.viewDetails')}
                      </Button>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {metrics.map((metric, idx) => {
                const bestValue = getBestValue(metric.key);
                const isOverallWinnerCol = (dealIdx) => deals[dealIdx]?.deal_score === Math.max(...deals.map(d => d.deal_score));
                return (
                  <tr key={metric.key} className={idx % 2 === 0 ? 'bg-white dark:bg-[#131824]' : 'bg-slate-50/50 dark:bg-[#0B0E14]/30'}>
                    <td className="p-4 font-medium text-sm text-slate-600 dark:text-slate-400 sticky left-0 bg-inherit z-10 border-r border-slate-100 dark:border-slate-800">
                      {metric.label}
                    </td>
                    {deals.map((deal, dealIdx) => {
                      const value = deal[metric.key];
                      const isBest = bestValue !== null && value === bestValue;
                      const isWinnerColumn = isOverallWinnerCol(dealIdx);
                      return (
                        <td
                          key={deal.deal_id}
                          className={`p-4 transition-colors ${isWinnerColumn ? 'bg-[#45C04A]/[0.03] dark:bg-[#45C04A]/[0.04]' : ''} ${isBest ? '!bg-[#45C04A]/[0.08] dark:!bg-[#45C04A]/[0.10]' : ''}`}
                        >
                          <div className="flex items-center gap-2">
                            <span className={`font-primary ${isBest ? 'font-bold text-[#12355B] dark:text-white text-base' : 'text-[#12355B] dark:text-slate-200 text-sm'} ${metric.key === 'monthly_cash_flow' && value < 0 ? '!text-red-500' : ''}`}>
                              {metric.format(value)}
                            </span>
                            {isBest && (
                              <span className="inline-flex items-center justify-center w-5 h-5 bg-[#45C04A] rounded-full flex-shrink-0">
                                <Check className="w-3 h-3 text-white" strokeWidth={3} />
                              </span>
                            )}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Winner Summary */}
        <div className="mt-10 mb-2">
          <h2 className="font-primary font-bold text-xl text-[#12355B] dark:text-white mb-5 flex items-center gap-2">
            <Trophy className="w-5 h-5 text-[#45C04A]" />
            {t('dealCompare.winnersTitle')}
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {(() => {
            const highestScore = deals.reduce((max, d) => (d.deal_score > max.deal_score ? d : max), deals[0]);
            const bestCashFlow = deals.reduce((max, d) => (d.monthly_cash_flow > max.monthly_cash_flow ? d : max), deals[0]);
            const bestROI = deals.reduce((max, d) => (d.roi > max.roi ? d : max), deals[0]);

            return (
              <>
                <div className="bg-gradient-to-br from-[#45C04A]/5 to-[#45C04A]/10 dark:from-[#45C04A]/10 dark:to-[#45C04A]/5 border-2 border-[#45C04A]/30 rounded-2xl p-6 relative overflow-hidden">
                  <div className="absolute top-3 right-3">
                    <span className="text-4xl font-primary font-black text-[#45C04A]/10">{t('dealCompare.rank.first')}</span>
                  </div>
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-9 h-9 bg-[#45C04A] rounded-xl flex items-center justify-center">
                      <BarChart3 className="w-5 h-5 text-white" />
                    </div>
                    <span className="font-primary font-bold text-[#12355B] dark:text-white">{t('dealCompare.highestScore')}</span>
                  </div>
                  <p className="text-sm text-slate-500 dark:text-slate-400 mb-3 line-clamp-2">{highestScore.property_address}</p>
                  <p className="text-4xl font-primary font-black text-[#45C04A]">{fmt.number(highestScore.deal_score)}</p>
                </div>

                <div className="bg-gradient-to-br from-[#45C04A]/5 to-[#45C04A]/10 dark:from-[#45C04A]/10 dark:to-[#45C04A]/5 border-2 border-[#45C04A]/30 rounded-2xl p-6 relative overflow-hidden">
                  <div className="absolute top-3 right-3">
                    <span className="text-4xl font-primary font-black text-[#45C04A]/10">{t('dealCompare.rank.first')}</span>
                  </div>
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-9 h-9 bg-[#45C04A] rounded-xl flex items-center justify-center">
                      <DollarSign className="w-5 h-5 text-white" />
                    </div>
                    <span className="font-primary font-bold text-[#12355B] dark:text-white">{t('dealCompare.bestCashFlow')}</span>
                  </div>
                  <p className="text-sm text-slate-500 dark:text-slate-400 mb-3 line-clamp-2">{bestCashFlow.property_address}</p>
                  <p className="text-4xl font-primary font-black text-[#45C04A]">
                    {fmt.money2(bestCashFlow.monthly_cash_flow)}<span className="text-lg text-slate-400">{t('dealCompare.perMonthSuffix')}</span>
                  </p>
                </div>

                <div className="bg-gradient-to-br from-[#45C04A]/5 to-[#45C04A]/10 dark:from-[#45C04A]/10 dark:to-[#45C04A]/5 border-2 border-[#45C04A]/30 rounded-2xl p-6 relative overflow-hidden">
                  <div className="absolute top-3 right-3">
                    <span className="text-4xl font-primary font-black text-[#45C04A]/10">{t('dealCompare.rank.first')}</span>
                  </div>
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-9 h-9 bg-[#45C04A] rounded-xl flex items-center justify-center">
                      <TrendingUp className="w-5 h-5 text-white" />
                    </div>
                    <span className="font-primary font-bold text-[#12355B] dark:text-white">{t('dealCompare.bestROI')}</span>
                  </div>
                  <p className="text-sm text-slate-500 dark:text-slate-400 mb-3 line-clamp-2">{bestROI.property_address}</p>
                  <p className="text-4xl font-primary font-black text-[#45C04A]">{fmt.percent(bestROI.roi)}</p>
                </div>
              </>
            );
          })()}
        </div>

        <ResultsDisclaimer />
      </div>

      <Footer />

      {/* Save Comparison Modal */}
      {showSaveModal && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setShowSaveModal(false)}
        >
          <div
            className="bg-white dark:bg-slate-800 rounded-lg shadow-2xl max-w-md w-full p-6 relative animate-in fade-in zoom-in-95 duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              data-testid="close-save-modal"
              onClick={() => setShowSaveModal(false)}
              className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="mb-5">
              <h3 className="font-primary font-bold text-xl text-[#12355B] dark:text-white">{t('dealCompare.saveComparison')}</h3>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{t('dealCompare.saveDesc')}</p>
            </div>
            <input
              data-testid="comparison-name-input"
              type="text"
              value={saveName}
              onChange={(e) => setSaveName(e.target.value)}
              placeholder={t('dealCompare.namePlaceholder')}
              className="w-full border border-slate-200 dark:border-slate-600 dark:bg-slate-700 dark:text-white rounded-sm px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 mb-4"
              maxLength={100}
              autoFocus
              onKeyDown={(e) => e.key === 'Enter' && saveComparison()}
            />
            <div className="flex gap-3 justify-end">
              <Button
                data-testid="cancel-save-button"
                onClick={() => setShowSaveModal(false)}
                variant="outline"
                className="rounded-sm"
              >
                {t('common.cancel')}
              </Button>
              <Button
                data-testid="confirm-save-button"
                onClick={saveComparison}
                disabled={saving || !saveName.trim()}
                className="bg-[#12355B] hover:bg-[#12355B]/90 dark:bg-[#45C04A] dark:hover:bg-[#65D95A] dark:text-[#12355B] text-white rounded-sm"
              >
                {saving ? t('common.saving') : t('common.save')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
